import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { X, Search, Minus, Plus } from "lucide-react";
import FormField from "../ui/FormField";
import { Button } from "../ui/Button";
import { Portal } from "../ui/Portal";
import { getSunbedCustomerPackages, getSunbedMemberships, getSunbedRooms, parseSunbedMinutes, SunbedCustomerPackageApi, SunbedMembershipApi, SunbedRoomApi } from "../../services/sunbeds";

function isSunbedRoomAvailable(room: SunbedRoomApi): boolean {
    const normalized = String(room.availability ?? "")
        .toLowerCase()
        .replace(/_/g, "-")
        .trim();
    return normalized === "available";
}

function formatRoomAvailability(status: string): string {
    const normalized = status.toLowerCase().replace(/_/g, "-");
    if (normalized === "available") return "Available";
    if (normalized === "in-session" || normalized === "in_session") return "In session";
    if (normalized === "cooling") return "Cooling";
    if (normalized === "maintenance") return "Maintenance";
    if (normalized === "cleaning") return "Cleaning";
    return status;
}

function formatRoomOptionLabel(room: SunbedRoomApi, showStatus: boolean): string {
    if (!showStatus || isSunbedRoomAvailable(room)) return room.label;
    return `${room.label} (${formatRoomAvailability(room.availability)})`;
}

/** Prefer live availability from company rooms list when merging package rooms. */
function mergeLiveRoomStatus(base: SunbedRoomApi[], live: SunbedRoomApi[]): SunbedRoomApi[] {
    if (!base.length) return live;
    if (!live.length) return base;
    const liveById = new Map(live.map((r) => [String(r.id), r]));
    return base.map((r) => {
        const fresh = liveById.get(String(r.id));
        return fresh
            ? {
                  ...r,
                  availability: fresh.availability,
                  tmax_id: fresh.tmax_id ?? r.tmax_id,
              }
            : r;
    });
}

interface CreateSessionDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    waitingListEnabled?: boolean;
    onAddToWaitingList?: (data: {
        customerId: number;
        packageId?: number;
        sunbedPackageId?: number;
        membershipId?: number;
        duration: string;
        delay: string;
        preferredRoomId?: string;
    }) => void | Promise<void>;
    addingToWaitingList?: boolean;
}

interface CustomerWithPackage {
    id: number;
    customerId: number;
    customerName: string;
    phone: string;
    packageName: string;
    remainingMinutes: number;
    displayLabel: string;
    /** Rooms currently free for Start Session. */
    availableRooms: SunbedRoomApi[];
    /** All rooms linked to this package/plan (includes cooling / in-session). */
    packageRooms: SunbedRoomApi[];
    /** Catalog sunbed id — used to scope live rooms. */
    sunbedId?: number | null;
    packageId?: number;
    sunbedPackageId?: number;
    membershipId?: number;
    source: "prepaid" | "membership";
    unlimited?: boolean;
    maxSessionMinutes?: number | null;
}

export const CreateSessionDrawer = ({
    isOpen,
    onClose,
    onSave,
    waitingListEnabled = false,
    onAddToWaitingList,
    addingToWaitingList = false,
}: CreateSessionDrawerProps) => {
    const [customer, setCustomer] = useState("");
    const [room, setRoom] = useState("");
    const [duration, setDuration] = useState("");
    const [delay, setDelay] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPackage, setSelectedPackage] = useState<CustomerWithPackage | null>(null);
    const [customersWithPackages, setCustomersWithPackages] = useState<CustomerWithPackage[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [allRooms, setAllRooms] = useState<SunbedRoomApi[]>([]);
    const allRoomsRef = useRef<SunbedRoomApi[]>([]);
    const fetchRequestIdRef = useRef(0);

    useEffect(() => {
        allRoomsRef.current = allRooms;
    }, [allRooms]);

    // Fetch all rooms so memberships without a room list can use every bed
    useEffect(() => {
        if (!isOpen) return;
        const fetchRooms = async () => {
            try {
                const res = await getSunbedRooms();
                if (res && Array.isArray(res.data)) {
                    setAllRooms(res.data);
                } else if (Array.isArray(res)) {
                    setAllRooms(res);
                }
            } catch (err) {
                console.error("Failed to fetch rooms", err);
            }
        };
        void fetchRooms();
    }, [isOpen]);

    const roomsByAvailability = useCallback((rooms: SunbedRoomApi[] = []) => {
        return rooms.filter((r) => String(r.availability).toLowerCase() === "available");
    }, []);

    const mapPrepaidRows = useCallback((packages: SunbedCustomerPackageApi[]): CustomerWithPackage[] => {
        return packages
            .map((pkg) => {
                const customerDetail = pkg.user;
                const phone = customerDetail?.mobile || customerDetail?.formatted_mobile || "";
                const customerName = customerDetail?.name || "Unknown";
                const packageName = pkg.package_name || pkg.sunbed?.label || "Sunbed Package";
                const remainingMinutes = parseSunbedMinutes(pkg.remaining_balance);

                return {
                    id: pkg.id,
                    customerId: pkg.user_id,
                    customerName,
                    phone,
                    packageName,
                    remainingMinutes,
                    displayLabel: `${customerName}${phone ? ` (${phone})` : ""} - ${packageName} (${remainingMinutes} mins)`,
                    packageRooms: pkg.sunbed?.rooms || [],
                    availableRooms: roomsByAvailability(pkg.sunbed?.rooms || []),
                    sunbedId: pkg.sunbed?.id ?? pkg.sunbed_id ?? null,
                    packageId: pkg.id,
                    sunbedPackageId: pkg.id,
                    source: "prepaid" as const,
                };
            })
            .filter((item) => item.customerName !== "Unknown" && item.remainingMinutes > 0);
    }, [roomsByAvailability]);

    const mapMembershipRows = useCallback((memberships: SunbedMembershipApi[], roomsFallback: SunbedRoomApi[]): CustomerWithPackage[] => {
        return memberships
            .filter((membership) => membership.status === "active")
            .map((membership) => {
                const remainingMinutes = membership.unlimited
                    ? 9999
                    : Number(membership.remaining_minutes ?? 0);
                const remainingLabel = membership.unlimited
                    ? "unlimited"
                    : `${remainingMinutes} mins`;
                const planRooms = (membership.rooms || []) as SunbedRoomApi[];
                const rooms = planRooms.length > 0 ? planRooms : roomsFallback;
                return {
                    id: membership.id + 1000000,
                    customerId: membership.user_id,
                    customerName: membership.customer || "Unknown",
                    phone: "",
                    packageName: membership.plan_name,
                    remainingMinutes,
                    displayLabel: `${membership.customer || "Customer"} - ${membership.plan_name} (${remainingLabel})`,
                    packageRooms: rooms,
                    availableRooms: roomsByAvailability(rooms),
                    membershipId: membership.id,
                    source: "membership" as const,
                    unlimited: membership.unlimited,
                    maxSessionMinutes: membership.max_session_minutes,
                };
            })
            .filter(
                (item) =>
                    item.customerName !== "Unknown" &&
                    (item.unlimited || item.remainingMinutes > 0)
            );
    }, [roomsByAvailability]);

    // Fetch customers once when drawer opens (do not re-fetch when rooms arrive)
    const fetchCustomersWithPackages = useCallback(async () => {
        const requestId = ++fetchRequestIdRef.current;
        setLoading(true);
        setCustomersWithPackages([]);

        try {
            const [packagesResult, membershipsResult] = await Promise.allSettled([
                getSunbedCustomerPackages(),
                getSunbedMemberships(),
            ]);

            if (requestId !== fetchRequestIdRef.current) return;

            const packages =
                packagesResult.status === "fulfilled"
                    ? packagesResult.value.data || []
                    : [];
            const memberships =
                membershipsResult.status === "fulfilled"
                    ? membershipsResult.value.data || []
                    : [];

            if (packagesResult.status === "rejected") {
                console.error("Failed to fetch prepaid packages:", packagesResult.reason);
            }
            if (membershipsResult.status === "rejected") {
                console.error("Failed to fetch memberships:", membershipsResult.reason);
            }

            const prepaid = mapPrepaidRows(packages);
            const membershipRows = mapMembershipRows(memberships, allRoomsRef.current);
            setCustomersWithPackages([...membershipRows, ...prepaid]);
        } catch (error) {
            if (requestId !== fetchRequestIdRef.current) return;
            console.error("Failed to fetch customers with packages:", error);
            setCustomersWithPackages([]);
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [mapPrepaidRows, mapMembershipRows]);

    // Effect for fetching customers when the drawer is open
    useEffect(() => {
        if (!isOpen) return;

        setCustomer("");
        setSelectedPackage(null);
        setRoom("");
        setDuration("");
        setDelay("");
        setSearchTerm("");
        setShowDropdown(false);
        void fetchCustomersWithPackages();
    }, [isOpen, fetchCustomersWithPackages]);

    // When rooms load later, refresh membership room options without toggling loading
    useEffect(() => {
        if (!isOpen || allRooms.length === 0) return;
        setCustomersWithPackages((prev) =>
            prev.map((item) => {
                if (item.source !== "membership") return item;
                if (item.availableRooms.length > 0) return item;
                return {
                    ...item,
                    availableRooms: roomsByAvailability(allRooms),
                };
            })
        );
    }, [allRooms, isOpen, roomsByAvailability]);

    // Memoized filtered customers
    const filteredCustomers = useMemo(() => {
        if (!searchTerm.trim()) {
            return customersWithPackages;
        }

        const term = searchTerm.toLowerCase();
        return customersWithPackages.filter(
            (item) =>
                item.customerName.toLowerCase().includes(term) ||
                item.phone.toLowerCase().includes(term) ||
                item.packageName.toLowerCase().includes(term)
        );
    }, [searchTerm, customersWithPackages]);

    const handleCustomerSelect = (selected: CustomerWithPackage) => {
        setCustomer(selected.displayLabel);
        setSelectedPackage(selected);
        setSearchTerm(""); // Clear search term as input is replaced
        setShowDropdown(false);
        setRoom(""); // Reset room when customer changes
    };

    const clearSelection = () => {
        setCustomer("");
        setSelectedPackage(null);
        setSearchTerm("");
        setRoom("");
    };

    const sessionAvailableRooms = useMemo(() => {
        if (!selectedPackage) return [];
        // Recompute free rooms from live allRooms so cooling updates are respected
        const packageRooms = selectedPackage.packageRooms ?? [];
        const liveScoped =
            selectedPackage.sunbedId != null && allRooms.length > 0
                ? allRooms.filter(
                      (r) => Number(r.sunbed_id) === Number(selectedPackage.sunbedId)
                  )
                : allRooms.length > 0 && packageRooms.length > 0
                  ? allRooms.filter((r) =>
                        packageRooms.some((pr) => String(pr.id) === String(r.id))
                    )
                  : [];

        const candidates =
            liveScoped.length > 0
                ? liveScoped
                : mergeLiveRoomStatus(packageRooms, allRooms);

        const liveAvailable = candidates.filter(isSunbedRoomAvailable);
        return liveAvailable.length > 0
            ? liveAvailable
            : selectedPackage.availableRooms.filter((r) => {
                  const live = allRooms.find((ar) => String(ar.id) === String(r.id));
                  return live ? isSunbedRoomAvailable(live) : isSunbedRoomAvailable(r);
              });
    }, [selectedPackage, allRooms]);

    // Waiting list / room picker: live company rooms scoped to this package's sunbed
    const waitingListRooms = useMemo(() => {
        if (!selectedPackage) return allRooms;

        if (selectedPackage.sunbedId != null && allRooms.length > 0) {
            const scoped = allRooms.filter(
                (r) => Number(r.sunbed_id) === Number(selectedPackage.sunbedId)
            );
            if (scoped.length > 0) return scoped;
        }

        const packageRooms = selectedPackage.packageRooms ?? [];
        if (packageRooms.length > 0 && allRooms.length > 0) {
            const ids = new Set(packageRooms.map((r) => String(r.id)));
            const liveScoped = allRooms.filter((r) => ids.has(String(r.id)));
            if (liveScoped.length > 0) return liveScoped;
            return mergeLiveRoomStatus(packageRooms, allRooms);
        }

        if (packageRooms.length > 0) return packageRooms;
        return allRooms;
    }, [selectedPackage, allRooms]);

    const roomsForDropdown = selectedPackage
        ? waitingListEnabled
            ? waitingListRooms
            : sessionAvailableRooms
        : allRooms;

    const selectedRoomObj = useMemo(() => {
        if (!room) return null;
        return (
            waitingListRooms.find((r) => r.id.toString() === room) ||
            allRooms.find((r) => r.id.toString() === room) ||
            sessionAvailableRooms.find((r) => r.id.toString() === room) ||
            null
        );
    }, [room, waitingListRooms, allRooms, sessionAvailableRooms]);

    const selectedRoomIsAvailable = Boolean(
        room &&
            selectedRoomObj &&
            isSunbedRoomAvailable(selectedRoomObj) &&
            sessionAvailableRooms.some((r) => r.id.toString() === room)
    );

    const canStartSession = Boolean(
        selectedPackage && room && selectedRoomIsAvailable
    );

    // Package or membership — show whenever waiting list feature is on.
    const canAddToWaitingList = Boolean(
        waitingListEnabled &&
            onAddToWaitingList &&
            selectedPackage &&
            (
                (selectedPackage.source === "prepaid" && selectedPackage.packageId) ||
                (selectedPackage.source === "membership" && selectedPackage.membershipId)
            )
    );

    const showWaitingListHint = Boolean(
        canAddToWaitingList && !canStartSession
    );

    const roomOptions = roomsForDropdown.map((r) => ({
        value: r.id.toString(),
        label: formatRoomOptionLabel(r, waitingListEnabled),
    }));

    const waitingListMessage = room && selectedRoomObj && !selectedRoomIsAvailable
        ? `“${selectedRoomObj.label}” is ${formatRoomAvailability(selectedRoomObj.availability).toLowerCase()}. Add this customer to the waiting list for that room.`
        : "All beds are busy or cooling. Add this customer to the waiting list and start them when a room is free.";

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const selectedCustomer = customersWithPackages.find(
            (c) => c.displayLabel === customer
        );
        if (!selectedCustomer) {
            toast.error("Please select a valid customer");
            return;
        }
        if (!room) {
            toast.error("Please select a room");
            return;
        }
        const durationMins = parseInt(duration, 10);
        if (!durationMins || durationMins < 1) {
            toast.error("Please enter session duration");
            return;
        }

        const selectedRoom = selectedCustomer.availableRooms.find(
            (r) => r.id.toString() === room
        );
        if (!selectedRoom) {
            toast.error("Selected room is not available for a new session");
            return;
        }

        onSave({
            customer: selectedCustomer.displayLabel,
            customerId: selectedCustomer.customerId,
            room,
            tmaxId: selectedRoom?.tmax_id,
            duration,
            delay,
            packageId: selectedCustomer.packageId,
            membershipId: selectedCustomer.membershipId,
            remainingBalance: selectedCustomer.unlimited ? 0 : selectedCustomer.remainingMinutes,
        });
        onClose();
    };

    const handleAddToWaitingList = async () => {
        if (!selectedPackage || !onAddToWaitingList) return;

        const isMembership = selectedPackage.source === "membership";
        if (isMembership && !selectedPackage.membershipId) {
            toast.error("Please select a valid membership.");
            return;
        }
        if (!isMembership && !selectedPackage.packageId) {
            toast.error("Please select a valid prepaid package.");
            return;
        }

        const durationMins = parseInt(duration, 10);
        if (!durationMins || durationMins < 1) {
            toast.error("Please enter session duration");
            return;
        }

        if (
            !selectedPackage.unlimited &&
            durationMins > selectedPackage.remainingMinutes
        ) {
            toast.error(
                `Duration cannot exceed remaining ${isMembership ? "membership" : "package"} minutes (${selectedPackage.remainingMinutes}).`
            );
            return;
        }

        await onAddToWaitingList({
            customerId: selectedPackage.customerId,
            packageId: isMembership ? undefined : selectedPackage.packageId,
            sunbedPackageId: isMembership ? undefined : selectedPackage.packageId,
            membershipId: isMembership ? selectedPackage.membershipId : undefined,
            duration,
            delay,
            preferredRoomId: room || undefined,
        });
    };

    // Effect for closing dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.customer-search-container')) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showDropdown]);

    return (
        <Portal>
            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-[2999] transition-opacity duration-300"
                    onClick={onClose}
                />
            )}

            {/* Drawer Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[3000] transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button (Floating like BookingDetailsDrawer) */}
                {isOpen && (
                    <button
                        aria-label="Close drawer"
                        className="fixed -left-14 top-9 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center text-[#885ABB] hover:bg-purple-50 transition z-[3001]"
                        onClick={onClose}
                    >
                        <X strokeWidth={3} />
                    </button>
                )}

                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="px-6 py-6 border-b border-gray-100 bg-white">
                        <h2 className="text-2xl font-semibold text-[#402B69]">
                            Create Session
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Select a customer and configure sunbed session
                        </p>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Customer Search Field */}
                        <div className="space-y-2 customer-search-container">
                            <label className="text-sm font-semibold text-[#402B69]">
                                Select Customer *
                            </label>
                            <div className="relative">
                                {!customer ? (
                                    <>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                            <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setShowDropdown(true);
                                                }}
                                                onFocus={() => setShowDropdown(true)}
                                                placeholder="Search by name, phone..."
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent"
                                            />
                                        </div>

                                        {/* Dropdown */}
                                        {showDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {loading && customersWithPackages.length === 0 ? (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        Loading customers...
                                                    </div>
                                                ) : filteredCustomers.length === 0 ? (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        No customers found
                                                    </div>
                                                ) : (
                                                    filteredCustomers.map((item) => (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            onClick={() => handleCustomerSelect(item)}
                                                            className="w-full text-left px-4 py-3 hover:bg-[#F3E8FF] transition-colors border-b border-gray-100 last:border-b-0"
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium text-gray-800">
                                                                    {item.customerName}
                                                                </span>
                                                                <span
                                                                    className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                                                        item.source === "membership"
                                                                            ? "bg-indigo-100 text-indigo-700"
                                                                            : "bg-emerald-100 text-emerald-700"
                                                                    }`}
                                                                >
                                                                    {item.source === "membership"
                                                                        ? "Membership"
                                                                        : "Package"}
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-0.5">
                                                                {item.packageName} •{" "}
                                                                {item.unlimited
                                                                    ? "Unlimited"
                                                                    : `${item.remainingMinutes} mins left`}
                                                            </div>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="p-3 bg-[#F3E8FF] rounded-lg border border-[#C4A6E1] text-sm text-[#885ABB] flex justify-between items-center">
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold">{customer.split("-")[0].trim()}</span>
                                                {selectedPackage && (
                                                    <span
                                                        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                                            selectedPackage.source === "membership"
                                                                ? "bg-indigo-100 text-indigo-700"
                                                                : "bg-emerald-100 text-emerald-700"
                                                        }`}
                                                    >
                                                        {selectedPackage.source === "membership"
                                                            ? "Membership"
                                                            : "Package"}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs opacity-75">
                                                {selectedPackage?.packageName} (
                                                {selectedPackage?.unlimited
                                                    ? "Unlimited"
                                                    : `${selectedPackage?.remainingMinutes} mins`}
                                                )
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={clearSelection}
                                            className="p-1 hover:bg-[#E0C8F6] rounded-full transition-colors text-[#885ABB] hover:text-[#5e3a85] focus:outline-none"
                                            title="Clear selection"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {showWaitingListHint && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                {waitingListMessage}
                            </div>
                        )}

                        <FormField
                            label={waitingListEnabled ? "Select Room *" : "Select Room *"}
                            name="room"
                            type="select"
                            value={room}
                            onChange={(_, val) => setRoom(val)}
                            options={roomOptions}
                            placeholder={
                                selectedPackage
                                    ? waitingListEnabled
                                        ? waitingListRooms.length > 0
                                            ? "-- Select Room --"
                                            : "No rooms configured"
                                        : sessionAvailableRooms.length > 0
                                          ? "-- Select Room --"
                                          : "No rooms available"
                                    : "-- Select customer first --"
                            }
                        />

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-[#402B69]">
                                Duration (minutes) *
                            </label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    aria-label="Decrease duration"
                                    onClick={() => {
                                        const n = Math.max(0, (parseInt(duration, 10) || 0) - 1);
                                        setDuration(String(n));
                                    }}
                                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:ring-offset-1"
                                >
                                    <Minus className="h-5 w-5" />
                                </button>
                                <input
                                    type="number"
                                    min={0}
                                    name="duration"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    placeholder="e.g. 15"
                                    className="flex-1 px-3 py-2.5 rounded-lg bg-gray-100 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm border border-transparent focus:border-gray-300 focus:ring-2 focus:ring-[#885ABB] hover:bg-gray-50 focus:bg-white"
                                />
                                <button
                                    type="button"
                                    aria-label="Increase duration"
                                    onClick={() => {
                                        const n = (parseInt(duration, 10) || 0) + 1;
                                        setDuration(String(n));
                                    }}
                                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:ring-offset-1"
                                >
                                    <Plus className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-[#402B69]">
                                Delay Duration (minutes)
                            </label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    aria-label="Decrease delay"
                                    onClick={() => {
                                        const n = Math.max(0, (parseInt(delay, 10) || 0) - 1);
                                        setDelay(String(n));
                                    }}
                                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:ring-offset-1"
                                >
                                    <Minus className="h-5 w-5" />
                                </button>
                                <input
                                    type="number"
                                    min={0}
                                    name="delay"
                                    value={delay}
                                    onChange={(e) => setDelay(e.target.value)}
                                    placeholder="e.g. 5"
                                    className="flex-1 px-3 py-2.5 rounded-lg bg-gray-100 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm border border-transparent focus:border-gray-300 focus:ring-2 focus:ring-[#885ABB] hover:bg-gray-50 focus:bg-white"
                                />
                                <button
                                    type="button"
                                    aria-label="Increase delay"
                                    onClick={() => {
                                        const n = (parseInt(delay, 10) || 0) + 1;
                                        setDelay(String(n));
                                    }}
                                    className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:ring-offset-1"
                                >
                                    <Plus className="h-5 w-5" />
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Delay before session starts (optional)</p>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3 rounded-b-none">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-gray-300 text-gray-700 hover:bg-white"
                            disabled={addingToWaitingList}
                        >
                            Cancel
                        </Button>
                        {canAddToWaitingList && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => void handleAddToWaitingList()}
                                disabled={addingToWaitingList}
                                className="border-[#E9D5FF] text-[#885ABB] hover:bg-[#F3E8FF]"
                            >
                                {addingToWaitingList ? "Adding..." : "Add to Waiting List"}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            onClick={handleSubmit}
                            disabled={!canStartSession || addingToWaitingList}
                            className="bg-[#885ABB] hover:bg-[#6f47a2] text-white min-w-[100px] disabled:opacity-50"
                        >
                            Start Session
                        </Button>
                    </div>
                </div>
            </div>
        </Portal>
    );
};
